import {
  Alert,
  Button,
  Checkbox,
  Code,
  Fieldset,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import {
  InlineError,
  SectionCard,
} from "../../components/ui";
import { Page } from "../../shell/Page";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { api, type ManagedUser, type Role } from "../../lib/api";
import { roleLabel } from "../../lib/roleLabels";

/** Mirrors PASSWORD_LOGIN_ROLES; the server enforces it and a DB trigger backs it. */
const PASSWORD_ROLES = ["ADMIN", "STORE_MANAGER"];
const usesPassword = (roles: readonly string[]) =>
  roles.some((role) => PASSWORD_ROLES.includes(role));

/** The order the checkboxes appear in when /admin/meta cannot be reached. */
const FALLBACK_ROLES: Role[] = [
  "ADMIN",
  "STORE_MANAGER",
  "MARKETING",
  "BILLING_EXECUTIVE",
  "PAYMENT_COLLECTION_AGENT",
  "DELIVERY_AGENT",
  "SERVICE_MANAGER",
  "INDOOR_SERVICE_TECHNICIAN",
  "OUTDOOR_SERVICE_TECHNICIAN",
];

/** Same order, same set, so two lists cannot get out of step. */
const sortRoles = (roles: readonly string[], order: readonly string[]): Role[] =>
  order.filter((role) => roles.includes(role)) as Role[];

/**
 * Create or edit an account.
 *
 * ONE PERSON, MANY ROLES
 *
 * Testing fed back that the mapping is many-to-many: the same person collects
 * batteries in the morning and diagnoses them in the afternoon. So this is a
 * set of checkboxes rather than a dropdown, and what is submitted is the
 * COMPLETE set the person should end up with - the server works out what to
 * grant and what to revoke.
 *
 * The roles decide how someone signs in, and therefore which identity field
 * this form asks for. That is not cosmetic: an account with no desk role must
 * have a mobile number and must NOT have a username, and one with a desk role
 * is the reverse - database constraints and two triggers enforce it, so a form
 * that let you submit the wrong combination would just be collecting a
 * rejection.
 *
 * The awkward case is revoking the last desk role from someone who already has
 * a password. That strips their username and password, which leaves them no
 * way in - so a mobile number is required in the same save. The form says so
 * before you submit rather than after.
 */
export function UserForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const editing = id !== undefined && id !== "new";

  const [existing, setExisting] = useState<ManagedUser>();
  /** Every role the installation offers, from /admin/meta. */
  const [available, setAvailable] = useState<Role[]>(FALLBACK_ROLES);
  /**
   * The roles ticked right now. Empty is not saveable, and empty is where a
   * NEW account starts - deliberately.
   *
   * This began life defaulting to ["MARKETING"], which looked harmless and
   * was not: the box was already ticked when the form opened, so an Admin who
   * ticked "Delivery Agent" and "Service Technician" created somebody with
   * three roles and a Marketing module nobody chose to give them. A default
   * that grants access is a default that has to be noticed to be undone.
   * Found by a browser drive reading the list afterwards.
   */
  const [selected, setSelected] = useState<Role[]>([]);
  const [name, setName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  /** Shown until dismissed: this is the only time anyone sees it. */
  const [issued, setIssued] = useState<{ name: string; username: string; password: string }>();

  useEffect(() => {
    void (async () => {
      try {
        const meta = await api.adminMeta();
        if (meta.roles.length > 0) setAvailable(meta.roles);
      } catch {
        // Falls back to FALLBACK_ROLES, which is the same list.
      }
      if (!editing || !id) return;
      try {
        const { user } = await api.adminUser(id);
        setExisting(user);
        setName(user.name);
        setSelected(user.roles);
        setMobileNumber(user.mobileNumber ?? "");
        setUsername(user.username ?? "");
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Could not load that user.");
      }
    })();
  }, [editing, id]);

  const nextUsesPassword = usesPassword(selected);
  const hasPassword = existing?.username !== null && existing?.username !== undefined;
  const existingRoles = existing?.roles ?? [];

  /**
   * Compared as SETS, in a fixed order, so ticking a role and unticking it
   * again is not read as a change - which would otherwise send a needless
   * grant-and-revoke pair to the server and write two audit entries.
   */
  const rolesChanged =
    existing !== undefined &&
    sortRoles(selected, available).join(",") !==
      sortRoles(existingRoles, available).join(",");

  function toggleRole(role: Role) {
    setSelected((current) =>
      current.includes(role)
        ? current.filter((held) => held !== role)
        : [...current, role],
    );
  }

  /** "Marketing and Service Technician", for the sentences below. */
  const selectedLabel =
    selected.length === 0
      ? "an account with no role"
      : selected.length === 1
        ? roleLabel(selected[0]!)
        : `${selected.slice(0, -1).map(roleLabel).join(", ")} and ${roleLabel(selected.at(-1)!)}`;

  /**
   * Why this cannot be saved yet, if it cannot. Computed rather than checked
   * on submit so the button explains itself while you are still typing.
   */
  const blocker = useMemo<string | undefined>(() => {
    if (name.trim().length < 2) return "Enter a name.";
    if (selected.length === 0) {
      return "Choose at least one role — an account with none can reach nothing.";
    }

    if (!editing) {
      if (nextUsesPassword && username.trim() === "") {
        return `A ${selectedLabel} signs in with a username, so one is required.`;
      }
      if (!nextUsesPassword && mobileNumber.trim() === "") {
        return `A ${selectedLabel} signs in with a mobile number, so one is required.`;
      }
      return undefined;
    }

    // Revoking the last desk role from an account that has a password: the
    // password goes, so a mobile has to arrive in the same save.
    if (rolesChanged && !nextUsesPassword && hasPassword && mobileNumber.trim() === "") {
      return (
        `${existing?.name} signs in with a username and password, which a ` +
        `${selectedLabel} account cannot do. Add a mobile number so they ` +
        `can still sign in.`
      );
    }
    if (!nextUsesPassword && mobileNumber.trim() === "") {
      return `A ${selectedLabel} needs a mobile number to sign in.`;
    }
    if (nextUsesPassword && mobileNumber.trim() === "" && !hasPassword) {
      return (
        `${existing?.name} has no password yet. Save the role change, then use ` +
        `"Issue a password" on this screen.`
      );
    }
    return undefined;
  }, [
    name, editing, nextUsesPassword, username, mobileNumber, selected.length,
    selectedLabel, rolesChanged, hasPassword, existing?.name,
  ]);

  const isSelf = existing !== undefined && existing.id === me?.id;

  async function save() {
    if (blocker || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      if (!editing) {
        const created = await api.createAdminUser({
          name: name.trim(),
          roles: sortRoles(selected, available),
          ...(mobileNumber.trim() ? { mobileNumber: mobileNumber.trim() } : {}),
          ...(nextUsesPassword && username.trim() ? { username: username.trim() } : {}),
        });
        if (created.temporaryPassword) {
          setIssued({
            name: created.user.name,
            username: created.user.username ?? username.trim(),
            password: created.temporaryPassword,
          });
          setBusy(false);
          return;
        }
        navigate("/admin/users", { replace: true });
        return;
      }

      await api.updateAdminUser(id!, {
        ...(name.trim() !== existing?.name ? { name: name.trim() } : {}),
        // The complete set, not a delta - the server diffs it.
        ...(rolesChanged ? { roles: sortRoles(selected, available) } : {}),
        ...(mobileNumber.trim() !== (existing?.mobileNumber ?? "")
          ? { mobileNumber: mobileNumber.trim() === "" ? null : mobileNumber.trim() }
          : {}),
        ...(nextUsesPassword &&
        hasPassword &&
        username.trim() !== (existing?.username ?? "")
          ? { username: username.trim() }
          : {}),
      });
      navigate("/admin/users", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save.");
      setBusy(false);
    }
  }

  async function issuePassword() {
    if (!editing || !id || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await api.issueUserPassword(
        id,
        username.trim() && username.trim() !== (existing?.username ?? "")
          ? username.trim()
          : undefined,
      );
      setIssued({
        name: result.user.name,
        username: result.user.username ?? username.trim(),
        password: result.temporaryPassword,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not issue a password.");
    } finally {
      setBusy(false);
    }
  }

  // --- the one-time password hand-off -----------------------------------
  if (issued) {
    return (
      <Page title={`Password for ${issued.name}`} subtitle="Write this down now">
        <Alert
          variant="light"
          color="yellow"
          radius="md"
          icon={<IconAlertTriangle size={18} stroke={1.8} />}
        >
          This is the only time this password is shown. It is stored only as a
          hash, so nobody — not an Admin, not us — can read it back. If it is
          lost, issue a new one.
        </Alert>
        <SectionCard title="Credentials">
          <Stack gap="sm">
            <Group justify="space-between" gap="md" wrap="nowrap">
              <Text fz="sm" c="gray.6">
                Username
              </Text>
              <Code fz="md">{issued.username}</Code>
            </Group>
            <Group justify="space-between" gap="md" wrap="nowrap">
              <Text fz="sm" c="gray.6">
                Password
              </Text>
              <Code fz="md">{issued.password}</Code>
            </Group>
            <Text fz="md" c="gray.7">
              {issued.name} will be asked to replace it the first time they
              sign in, and cannot use anything else in the app until they do.
            </Text>
            <Button
              size="md"
              fullWidth
              onClick={() => navigate("/admin/users", { replace: true })}
            >
              I have written it down
            </Button>
          </Stack>
        </SectionCard>
      </Page>
    );
  }

  return (
    <Page
      title={editing ? "Edit user" : "Add user"}
      subtitle={editing ? (existing?.name ?? undefined) : "A new staff account"}
      back="/admin/users"
    >
      {error && <InlineError>{error}</InlineError>}

      <SectionCard title="The account">
        <Stack gap="sm">
          <TextInput
            id="user-name"
            label="Name"
            withAsterisk
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.currentTarget.value)}
          />

          {/* A checkbox per role, not a dropdown: one person can hold several,
              and the same human should not need a second account to do a
              second job. Rendered as a fieldset so a screen reader announces
              the group and its legend once, rather than nine unrelated
              checkboxes. */}
          <Fieldset legend="Roles" className="roles" disabled={isSelf} p="sm">
            <Stack gap="xs">
              {available.map((option) => (
                <Checkbox
                  key={option}
                  id={`role-${option}`}
                  checked={selected.includes(option)}
                  onChange={() => toggleRole(option)}
                  label={
                    <>
                      {roleLabel(option)}
                      {PASSWORD_ROLES.includes(option) && (
                        <Text component="span" fz="sm" c="gray.6">
                          {" "}
                          — username and password
                        </Text>
                      )}
                    </>
                  }
                />
              ))}
            </Stack>
          </Fieldset>
          <Text fz="sm" c="gray.6">
            {isSelf
              ? "You cannot change your own roles. Ask another Admin to do it."
              : selected.length === 0
                ? "Tick every job this person does."
                : nextUsesPassword
                  ? "Signs in with a username and password."
                  : "Signs in with a mobile number and a one-time code."}
          </Text>

          {/* Mobile is shown whatever the roles: a desk role may also have
              one, and losing the last desk role requires it. */}
          <TextInput
            id="user-mobile"
            label="Mobile number"
            withAsterisk={!nextUsesPassword}
            type="tel"
            inputMode="tel"
            className="num"
            value={mobileNumber}
            placeholder="10 digits"
            onChange={(event) => setMobileNumber(event.currentTarget.value)}
            description={
              nextUsesPassword
                ? "Optional for these roles — they sign in with a password. Useful only if they might later become field staff."
                : undefined
            }
          />

          {nextUsesPassword && (
            <TextInput
              id="user-username"
              label="Username"
              withAsterisk={!editing}
              autoComplete="off"
              value={username}
              placeholder="name@harshacorp.local"
              disabled={editing && !hasPassword}
              onChange={(event) => setUsername(event.currentTarget.value)}
              description={
                editing && !hasPassword
                  ? 'This account has no password yet — use "Issue a password" below to set both at once.'
                  : "Letters, numbers and . _ + - @ — it is an identifier, not an email address."
              }
            />
          )}

          {blocker && (
            <Alert
              variant="light"
              color="yellow"
              radius="md"
              p="sm"
              icon={<IconAlertTriangle size={18} stroke={1.8} />}
            >
              {blocker}
            </Alert>
          )}

          <Button
            size="md"
            fullWidth
            loading={busy}
            disabled={blocker !== undefined}
            onClick={() => void save()}
          >
            {editing ? "Save changes" : "Create account"}
          </Button>
        </Stack>
      </SectionCard>

      {editing && existing && usesPassword(existingRoles) && (
        <SectionCard title="Password">
          <Stack gap="sm">
            <Text fz="md" c="gray.7">
              {existing.passwordUpdatedAt
                ? `Last set ${new Date(existing.passwordUpdatedAt).toLocaleDateString()}.`
                : "Never set."}{" "}
              There is no self-service reset, so this is the only way to recover
              a forgotten one.
            </Text>
            <Button
              variant="default"
              size="md"
              fullWidth
              loading={busy}
              onClick={() => void issuePassword()}
            >
              Issue a new password
            </Button>
          </Stack>
        </SectionCard>
      )}

      {editing &&
        existing &&
        !usesPassword(existingRoles) &&
        rolesChanged &&
        nextUsesPassword && (
          <SectionCard>
            <Text fz="md" c="gray.7">
              Save the role change first. A password can then be issued from
              this screen.
            </Text>
          </SectionCard>
        )}
    </Page>
  );
}
